/**
 * Deals Page - Pipeline View with full CRUD
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/utils';
import { Plus, Pencil, Trash2 } from 'lucide-react';

interface PipelineStage {
  stage: string;
  dealCount: number;
  totalValue: number;
  weightedValue: number;
}

interface Deal {
  id: string;
  accountId: string;
  accountName: string;
  contactId: string | null;
  contactName: string | null;
  title: string;
  description: string | null;
  value: number;
  stage: string;
  probability: number;
  expectedCloseDate: string | null;
  dealType: string | null;
  ownerName: string | null;
  lostReason: string | null;
  notes: string | null;
}

interface AccountLite {
  id: string;
  name: string;
}

const stageLabels: Record<string, string> = {
  lead: 'Lead',
  qualified: 'Qualified',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

const stageColors: Record<string, string> = {
  lead: 'bg-gray-100 border-gray-300',
  qualified: 'bg-blue-50 border-blue-300',
  proposal: 'bg-yellow-50 border-yellow-300',
  negotiation: 'bg-orange-50 border-orange-300',
  closed_won: 'bg-green-50 border-green-300',
  closed_lost: 'bg-red-50 border-red-300',
};

const defaultProbabilities: Record<string, number> = {
  lead: 10,
  qualified: 25,
  proposal: 50,
  negotiation: 75,
  closed_won: 100,
  closed_lost: 0,
};

const openStages = ['lead', 'qualified', 'proposal', 'negotiation'];
const allStages = ['lead', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

export function DealsPage() {
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDeal, setDeletingDeal] = useState<Deal | null>(null);
  const [showClosed, setShowClosed] = useState(false);

  // Form state
  const [formAccountId, setFormAccountId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formValue, setFormValue] = useState('');
  const [formStage, setFormStage] = useState('lead');
  const [formProbability, setFormProbability] = useState('10');
  const [formExpectedCloseDate, setFormExpectedCloseDate] = useState('');
  const [formDealType, setFormDealType] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formLostReason, setFormLostReason] = useState('');

  const { data: pipelineData, isLoading: pipelineLoading } = useQuery({
    queryKey: ['pipeline'],
    queryFn: async () => {
      const result = await api.getPipelineSummary();
      return result;
    },
  });

  const { data: dealsData, isLoading: dealsLoading } = useQuery({
    queryKey: ['deals'],
    queryFn: async () => {
      const result = await api.getDeals({ limit: 200 });
      return result;
    },
  });

  const { data: accountsData } = useQuery({
    queryKey: ['accounts-list'],
    queryFn: async () => {
      const result = await api.getAccounts({ limit: 100 });
      return result;
    },
  });

  const pipeline = (pipelineData?.data || []) as PipelineStage[];
  const deals = (dealsData?.data || []) as Deal[];
  const accounts = (accountsData?.data || []) as AccountLite[];

  const totalPipelineValue = pipeline.reduce((sum, s) => sum + s.totalValue, 0);
  const totalWeightedValue = pipeline.reduce((sum, s) => sum + s.weightedValue, 0);

  const getDealsByStage = (stage: string) => deals.filter((d) => d.stage === stage);

  function invalidateDeals() {
    queryClient.invalidateQueries({ queryKey: ['deals'] });
    queryClient.invalidateQueries({ queryKey: ['pipeline'] });
  }

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createDeal(data as Parameters<typeof api.createDeal>[0]),
    onSuccess: () => {
      invalidateDeals();
      closeDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateDeal(id, data),
    onSuccess: () => {
      invalidateDeals();
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDeal(id),
    onSuccess: () => {
      invalidateDeals();
      setDeleteDialogOpen(false);
      setDeletingDeal(null);
    },
  });

  function openCreateDialog(presetStage?: string) {
    setEditingDeal(null);
    setFormAccountId('');
    setFormTitle('');
    setFormValue('');
    setFormStage(presetStage || 'lead');
    setFormProbability(String(defaultProbabilities[presetStage || 'lead'] || 10));
    setFormExpectedCloseDate('');
    setFormDealType('');
    setFormDescription('');
    setFormNotes('');
    setFormLostReason('');
    setDialogOpen(true);
  }

  function openEditDialog(deal: Deal) {
    setEditingDeal(deal);
    setFormAccountId(deal.accountId);
    setFormTitle(deal.title);
    setFormValue(deal.value ? deal.value.toString() : '');
    setFormStage(deal.stage);
    setFormProbability(deal.probability.toString());
    setFormExpectedCloseDate(deal.expectedCloseDate ? deal.expectedCloseDate.split('T')[0] : '');
    setFormDealType(deal.dealType || '');
    setFormDescription(deal.description || '');
    setFormNotes(deal.notes || '');
    setFormLostReason(deal.lostReason || '');
    setDialogOpen(true);
  }

  function openDeleteDialog(deal: Deal) {
    setDeletingDeal(deal);
    setDeleteDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingDeal(null);
  }

  function handleStageChange(newStage: string) {
    setFormStage(newStage);
    setFormProbability(String(defaultProbabilities[newStage] || 0));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: Record<string, unknown> = {
      accountId: formAccountId,
      title: formTitle,
      value: formValue ? parseFloat(formValue) : 0,
      stage: formStage,
      probability: parseInt(formProbability) || 0,
      expectedCloseDate: formExpectedCloseDate || null,
      dealType: formDealType || null,
      description: formDescription || null,
      notes: formNotes || null,
      lostReason: formStage === 'closed_lost' ? (formLostReason || null) : null,
    };

    if (editingDeal) {
      updateMutation.mutate({ id: editingDeal.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (pipelineLoading || dealsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const visibleStages = showClosed ? allStages : openStages;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deals Pipeline</h1>
          <p className="text-muted-foreground">Manage your sales opportunities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowClosed(!showClosed)}>
            {showClosed ? 'Hide Closed' : 'Show Closed'}
          </Button>
          <Button onClick={() => openCreateDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            New Deal
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Pipeline Value</p>
            <p className="text-2xl font-bold">{formatCurrency(totalPipelineValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Weighted Value</p>
            <p className="text-2xl font-bold">{formatCurrency(totalWeightedValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Open Deals</p>
            <p className="text-2xl font-bold">{deals.filter((d) => !d.stage.startsWith('closed')).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Board */}
      <div className={`grid gap-4 ${showClosed ? 'md:grid-cols-6' : 'md:grid-cols-4'}`}>
        {visibleStages.map((stage) => {
          const stageDeals = getDealsByStage(stage);
          const stageTotal = stageDeals.reduce((sum, d) => sum + d.value, 0);

          return (
            <div key={stage} className={`rounded-lg border-2 ${stageColors[stage]} p-4`}>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="font-medium">{stageLabels[stage]}</h3>
                  <p className="text-sm text-muted-foreground">
                    {stageDeals.length} deal{stageDeals.length !== 1 ? 's' : ''} &bull; {formatCurrency(stageTotal)}
                  </p>
                </div>
                {!stage.startsWith('closed') && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openCreateDialog(stage)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {stageDeals.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No deals</p>
                ) : (
                  stageDeals.map((deal) => (
                    <Card key={deal.id} className="group cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-1">
                          <h4 className="font-medium text-sm truncate flex-1">{deal.title}</h4>
                          <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditDialog(deal)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => openDeleteDialog(deal)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{deal.accountName}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm font-medium">{formatCurrency(deal.value)}</span>
                          <span className="text-xs text-muted-foreground">{deal.probability}%</span>
                        </div>
                        {deal.expectedCloseDate && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Close: {new Date(deal.expectedCloseDate).toLocaleDateString()}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingDeal?.id || 'new'} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDeal ? 'Edit Deal' : 'New Deal'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Deal Title *</Label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Website Redesign"
                required
              />
            </div>

            <div>
              <Label>Account *</Label>
              <Select value={formAccountId} onValueChange={setFormAccountId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Value ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formValue}
                  onChange={(e) => setFormValue(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Deal Type</Label>
                <Select value={formDealType || 'none'} onValueChange={(v) => setFormDealType(v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="new_business">New Business</SelectItem>
                    <SelectItem value="renewal">Renewal</SelectItem>
                    <SelectItem value="upsell">Upsell</SelectItem>
                    <SelectItem value="cross_sell">Cross-Sell</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stage</Label>
                <Select value={formStage} onValueChange={handleStageChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allStages.map((s) => (
                      <SelectItem key={s} value={s}>{stageLabels[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Probability (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formProbability}
                  onChange={(e) => setFormProbability(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label>Expected Close Date</Label>
              <Input
                type="date"
                value={formExpectedCloseDate}
                onChange={(e) => setFormExpectedCloseDate(e.target.value)}
              />
            </div>

            {formStage === 'closed_lost' && (
              <div>
                <Label>Lost Reason</Label>
                <Input
                  value={formLostReason}
                  onChange={(e) => setFormLostReason(e.target.value)}
                  placeholder="Why was this deal lost?"
                />
              </div>
            )}

            <div>
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Deal details..."
                rows={2}
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                placeholder="Internal notes..."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || !formTitle || !formAccountId}>
                {isSaving ? 'Saving...' : editingDeal ? 'Update Deal' : 'Create Deal'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Deal</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingDeal?.title}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingDeal && deleteMutation.mutate(deletingDeal.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
